import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import Select from "react-select";
import { LoadingPortContext, resolvePageAccess } from "../../context/LoadingPortContext";
import { AgGridReact } from "@ag-grid-community/react";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { EditorAttachFile } from "material-ui/svg-icons";
import { Delete, Edit, Trash } from "lucide-react"
import redirectURL from "../redirectURL.js";
import XLSX from "xlsx/dist/xlsx.full.min.js";
import SweetAlert from 'react-bootstrap-sweetalert';
import { getCellValue, findByFieldIgnoreCase, isSameIgnoreCase, getUserIdentityFields } from "../common/utils";

const HubSourceMapping = () => {

    const gridRef = useRef();
    const portContext = useContext(LoadingPortContext);
    const lastPortRef = useRef(portContext.loading_port_code);

    const modules = [
        ClientSideRowModelModule,
        RowGroupingModule,
        MenuModule
    ];

    // ---------------- SLIDER STATE ----------------
    const [showSlider, setShowSlider] = useState(false);
    var allowActionsRef = useRef(false);
    var allowDeleteRef = useRef(false);
    // ---------------- DROPDOWN ----------------
    const loadingPorts = [
        { value: "CHN", label: "Chennai" },
        { value: "MUM", label: "Mumbai" },
        { value: "KOL", label: "Kolkata" }
    ];

    // ---------------- FORM ----------------
    const [formData, setFormData] = useState({
        loading_port_code: null,
        hub_name: "",
        consignee_city: "",
        state: ""
    });

    const [hubData, setHubData] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [originalEditData, setOriginalEditData] = useState(null);
    const [loadingPortData, setLoadingPortData] = useState([])
    const [showBulkSlider, setShowBulkSlider] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [consigneeCityData, setConsigneeCityData] = useState([]);
    // consigneeCityData has one record per (city, route) since lmconsignees stores
    // multiple routes for the same city — dedupe by city for the dropdown options only,
    // other lookups (state derivation, identity checks) still need the raw list.
    const uniqueConsigneeCityData = useMemo(() => {
        const seenCities = [];
        return consigneeCityData.filter((each) => {
            if (seenCities.indexOf(each.city) !== -1) return false;
            seenCities.push(each.city);
            return true;
        });
    }, [consigneeCityData]);
    const [stateData, setStateData] = useState([])

    const [hubMappingData, setHubMappingData] = useState([])
    const handleChange = e =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handlePortChange = (selected) => {
        // console.log(selected)
        setFormData({ ...formData, loading_port_code: selected.value, comp_short_name: selected.label });
    }

    // ---------------- VALIDATION ----------------
    const showValidationAlert = (message) => {
        setShow(true);
        setBasicType("warning");
        setBasicTitle(message);
    };

    const validateForm = () => {

        if (!formData.loading_port_code) {
            showValidationAlert("Select Loading Port");
            return false;
        }

        if (!formData.hub_name) {
            showValidationAlert("Select Hub");
            return false;
        }

        if (!formData.consignee_city) {
            showValidationAlert("Select Consignee City");
            return false;
        }

        if (!formData.state) {
            showValidationAlert("Select State");
            return false;
        }

        return true;
    };

    // ---------------- ADD / UPDATE ----------------
    const handleAdd = async () => {

        if (!validateForm()) return;

        if (editIndex !== null && originalEditData) {
            var hasChanges = ["hub_name", "consignee_city", "state"].some(
                (field) => String(originalEditData[field] || "") !== String(formData[field] || "")
            );
            if (!hasChanges) {
                showValidationAlert("No changes found. Please modify at least one field before updating.");
                return;
            }
        }

        const existingHub = hubMappingData.find(
            item =>
                item.consignee_city.toLowerCase() === formData.consignee_city.toLowerCase() &&
                item.state.toLowerCase() === formData.state.toLowerCase()
        );

        if (existingHub) {
            showValidationAlert(
                `${formData.consignee_city}, ${formData.state} is already mapped to hub "${existingHub.hub_name}".`
            );
            return;
        }

        const payload = {
            loading_port_code: formData.loading_port_code,
            comp_short_name: formData.comp_short_name,
            hub_name: formData.hub_name,
            consignee_city: formData.consignee_city,
            state: formData.state,
            is_active: 1,
            ...getUserIdentityFields()
        };
        setLoadshow("show-m")
        setOverly("show-m")
        await updateHubMappingData([payload], editIndex !== null ? "update" : "add")
        await getHubMappingData();

        closeSlider();
        setLoadshow("show-n")
        setOverly("show-n")
    };

    const closeSlider = () => {
        setShowSlider(false);
        setEditIndex(null);
        setOriginalEditData(null);
        setFormData({
            loading_port_code: null,
            hub_name: "",
            latitude: "",
            longitude: ""
        });
        setShowBulkSlider(false)
        setValidData([])
                        setErrors([])
                        setFileName("")
    };

    // ---------------- EDIT ----------------
    const handleEdit = (params) => {
        setFormData(params.data);
        setOriginalEditData(params.data);
        setEditIndex(params.rowIndex);
        setShowSlider(true);
    };

    // ---------------- DELETE ----------------
    const handleDelete = async (params) => {
        const confirmDelete = window.confirm(
            "Are you sure to delete this record?"
        );

        // ✅ Exit function if Cancel clicked
        if (!confirmDelete) return;
        const payload = {
            loading_port_code: params.data.loading_port_code,
            hub_name: params.data.hub_name,
            consignee_city: params.data.consignee_city,
            state: params.data.state,
            is_active: 1,  // usually delete = deactivate
            ...getUserIdentityFields()
        };
        setLoadshow("show-m")
        setOverly("show-m")
        console.log(payload);
        await deleteHubMappingData([payload])
        await getHubMappingData(params.data.loading_port_code);
        setLoadshow("show-n")
        setOverly("show-n")
    };



    const ActionRenderer = (props) => (
        <div className="d-flex justify-content-center align-items-center" style={{ gap: '6px', justifyContent: "center", width: "100%", height: "35px" }}>

            {allowActionsRef.current && <Edit className="text-primary cursor-pointer d-none"
                size={15} style={{ cursor: "pointer" }} onClick={() => { handleEdit(props) }} />}
            {allowDeleteRef.current && <Trash className="text-danger cursor-pointer ml-3"
                size={15} style={{ cursor: "pointer" }} onClick={() => { handleDelete(props) }} />}
        </div>
    );
    // ---------------- GRID ----------------
    const components = {
        actionRenderer: ActionRenderer
    };

    const columnDefs = [
        {
            headerName: "",
            width: 100,
            cellRenderer: "actionRenderer",
            pinned: "left",
            headerCheckboxSelection: allowDeleteRef.current ? true : false,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: allowDeleteRef.current ? true : false,
            hide: (!allowActionsRef.current && !allowDeleteRef.current)
        },
        // { headerName: "Source", field: "comp_short_name", flex: 1 },
        { headerName: "Hub", field: "hub_name", flex: 1 },
        { headerName: "Consignee City", field: "consignee_city", flex: 1 },
        { headerName: "State", field: "state", flex: 1 }
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoadshow("show-m")
            setOverly("show-m")
            var port = await pageAccessFn();
            await getNecessaryData(port);
            setLoadshow("show-n")
            setOverly("show-n")
        }
        fetchData();
    }, []);

    const getNecessaryData = async (port) => {
        await getHubData({ is_active: 1, loading_port_code: port })
        await getConsigneeCityData({ loading_port_code: port, status: 1 })
        await getHubMappingData(port);
    }

    const getHubMappingData = async (port) => {
        try {
            var params = {
                loading_port_code: port || loadingPortCode,
                is_active: 1
            }
            const response = await redirectURL.post("/masters/getHubConsigneeCity", params);
            if (response.data.status == "success") {
                setHubMappingData(response.data.hubMappingData || [])
            } else {

            }
        } catch (error) {
            console.log(error)
        }
    }
    const getHubData = async (params) => {
        try {
            const response = await redirectURL.post("/masters/getHubMasterData", params);
            if (response.data.status == "success") {
                setHubData(response.data.hubData
                    .map((each) => {
                        return { ...each, label: each.hub_name, value: each.hub_name }
                    })
                    || [])
            } else {

            }
        } catch (error) {
            console.log(error)
        }
    }
    const getLoadingPortCodeData = async () => {
        try {
            var params = {

            }
            const response = await redirectURL.post("/lmconsignees/getLmLoadingPorts", params);
            // console.log(response)
            if (response.status == "200") {
                // console.log(response)
                setLoadingPortData(
                    response.data
                        ? response.data.map((each) => ({
                            ...each,
                            label: each.comp_short_name,
                            value: each.loading_port_code
                        }))
                        : []
                );
            } else {

            }
        } catch (error) {
            console.log(error)
        }
    }
    const getConsigneeCityData = async (params) => {
        try {
            const response = await redirectURL.post("/lmconsignees/getLmConsignees", params);
            // console.log(response)
            if (response.status == "200") {
                setConsigneeCityData(response.data
                    .map((each) => {
                        return { ...each, label: each.city, value: each.city }
                    })
                    || [])
            } else {

            }
        } catch (error) {
            console.log(error)
        }
    }
    const updateHubMappingData = async (params, type) => {
        try {

            const response = await redirectURL.post("/masters/updateHubConsigneeCity", params);
            if (response.data.status == "success") {
                // setRowData(response.data.hubData || [])
                setShow(true)
                setBasicType("success")
                setBasicTitle(type === "add" ? "Added Successfully." : "Updated Successfully.")
            } else {
                setShow(true)
                setBasicType("warning")
                setBasicTitle(type === "add" ? "Failed to Add." : "Failed to update.")
            }
        } catch (error) {
            console.log(error)
        }
    }
    const deleteHubMappingData = async (payload) => {
        try {


            var params = payload
            const response = await redirectURL.post("/masters/deleteHubConsigneeCity", params);
            if (response.data.status == "success") {
                setSelectedRows([])
                setShow(true)
                setBasicType("success")
                setBasicTitle("Deleted Successfully.")
                // setRowData(response.data.hubData || [])
            } else {
                setShow(true)
                setBasicType("warning")
                setBasicTitle("Failed to Delete.")
            }
        } catch (error) {
            console.log(error)
        }
    }
    const handleBulkUpload = async () => {

        try {
            setLoadshow("show-m")
            setOverly("show-m")
            // check duplicates before upload
            // const duplicateRecords = [];

            // validData.forEach((row) => {

            //     const existingHub = hubMappingData.find(
            //         (item) =>
            //             item.consignee_city.trim().toLowerCase() === row.consignee_city.trim().toLowerCase() &&
            //             item.state.trim().toLowerCase() === row.state.trim().toLowerCase()
            //     );

            //     if (existingHub) {
            //         duplicateRecords.push(
            //             `${row.consignee_city}, ${row.state} already mapped to hub "${existingHub.hub_name}"`
            //         );
            //     }

            // });

            // if (duplicateRecords.length > 0) {
            //     alert(duplicateRecords.join("\n"));
            //     setLoadshow("show-n")
            //     setOverly("show-n")
            //     return;
            // }
            const finalData = validData.map((each) => {
                return (
                    {
                        ...each,
                        loading_port_code: loadingPortCode,
                        comp_short_name: loadingPortData.find((each) => each.value == loadingPortCode).label || "",
                        is_active: 1,
                        ...getUserIdentityFields()
                    }
                )
            })
            console.log(validData, finalData)

            await updateHubMappingData(finalData)
            await getHubMappingData(loadingPortCode);
            setLoadshow("show-n")
            setOverly("show-n")
            setValidData([])
            setErrors([])
            setShowBulkSlider(false);
            setBulkFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }


            // alert("Upload Successful");

        } catch (err) {
            console.error(err);
        }
    };
    const downloadTemplate = async () => {
        const response = await redirectURL.post(
            "/masters/downloadHubTemplate",
            {}, // body if needed
            { responseType: "blob" } // important for Excel download
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Hub_Bulk_Upload_Template.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleDeleteSelected = async (port) => {
        const confirmDelete = window.confirm(
            "Are you sure to delete this record?"
        );

        // ✅ Exit function if Cancel clicked
        if (!confirmDelete) return;
        var payload = selectedRows.map((each) => {
            return {
                loading_port_code: each.loading_port_code,
                hub_name: each.hub_name,
                consignee_city: each.consignee_city,
                state: each.state,
                is_active: 1,
                ...getUserIdentityFields()
            }
        })
        setLoadshow("show-m")
        setOverly("show-m")
        await deleteHubMappingData(payload)
        await getHubMappingData(port);
        setLoadshow("show-n")
        setOverly("show-n")
    }
    const [selectedRows, setSelectedRows] = useState([]);
    // console.log(loadingPortData, editIndex)
    const customStyles = {
        control: (provided) => ({
            ...provided,
            cursor: editIndex !== null ? "not-allowed" : "pointer"
        }),
        valueContainer: (provided) => ({
            ...provided,
            cursor: editIndex !== null ? "not-allowed" : "pointer"
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            cursor: editIndex !== null ? "not-allowed" : "pointer"
        }),
        container: (provided) => ({
            ...provided,
            cursor: editIndex !== null ? "not-allowed" : "pointer"
        })
    }


    useEffect(() => {
        if (!formData.consignee_city) return;

        const states = [
            ...new Set(
                consigneeCityData
                    .filter(each => each.city === formData.consignee_city)
                    .map(each => each.state)
            )
        ];

        console.log(states);
        if (states.length > 0 && states.length < 2) {
            setFormData((prevState) => ({ ...prevState, state: states[0] }))
        }
        setStateData(states.map((each) => ({ label: each, value: each })));

    }, [formData.consignee_city]);

    const [loadshow, setLoadshow] = useState("show-n")
    const [overly, setOverly] = useState("show-n")
    const [pageAccess, setPageAccess] = useState(0);
    const [loadingPortCode, setLoadingPortCode] = useState("");
    const [compCode, setCompCode] = useState("");
    const [lpNames, setLpNames] = useState([]);
    const [errors, setErrors] = useState([]);
    const [validData, setValidData] = useState([]);
    const [fileName, setFileName] = useState("");

    const fileInputRef = useRef(null);
    const onClickHideAll = () => {
        // setLoadshow("show-n")
        // setOverly("show-n")
        setValidData([])
        setErrors([])
        setFileName("")
    }
    const pageAccessFn = useCallback(async () => {

        setLoadshow("show-m");
        setOverly("show-m");

        const currentUrl = window.location.pathname;

        try {
            const resolved = await resolvePageAccess(currentUrl, portContext);
            if (!resolved) {
                // No access (already alerted+redirected), empty port list, or a logged request error.
                return;
            }
            lastPortRef.current = resolved.loading_port_code;
            setPageAccess(resolved.pageAccess);
            setLoadingPortCode(resolved.loading_port_code);
            setCompCode(resolved.comp_code);
            setLoadingPortData(resolved.lpNames);
            return resolved.loading_port_code;
        } catch (err) {
            console.log(err);
        }

    }, [portContext]);

    // React to the loading port changing from the header's global selector.
    useEffect(() => {
        if (portContext.loading_port_code !== lastPortRef.current) {
            lastPortRef.current = portContext.loading_port_code;
            onGlobalPortChange(portContext.loading_port_code);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portContext.loading_port_code]);

    const onGlobalPortChange = async (selectedPlant) => {
        if (!selectedPlant || !loadingPortData.some((each) => each.value == selectedPlant)) {
            // Not a valid port for this screen - keep this screen's own selection.
            return;
        }
        setLoadshow("show-m");
        setOverly("show-m");
        setLoadingPortCode(selectedPlant);
        await getNecessaryData(selectedPlant);
        setValidData([]);
        setErrors([]);
        setShowBulkSlider(false);
        setBulkFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setLoadshow("show-n");
        setOverly("show-n");
    };

    const handleFileChange1 = (e) => {

        const file = e.target.files[0];

        if (!file) return;

        const reader = new FileReader();

        reader.readAsArrayBuffer(file);

        reader.onload = (event) => {

            const workbook = XLSX.read(
                new Uint8Array(event.target.result),
                { type: "array" }
            );

            const sheet =
                workbook.Sheets[workbook.SheetNames[0]];

            const data =
                XLSX.utils.sheet_to_json(sheet, { defval: "" });

            validateExcelData(data);
        };
    };
    const handleFileChange = (e) => {

        const file = e.target.files[0];
        e.target.value = ""; // reset immediately

        if (!file) return;
        setFileName(file.name); // store file name

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onload = (event) => {

            const workbook = XLSX.read(
                new Uint8Array(event.target.result),
                { type: "array" }
            );

            const sheet = workbook.Sheets[workbook.SheetNames[0]];

            const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });

            validateExcelData(data);
        };
    };

    // ================= VALIDATION =================


    const validateExcelData = (data) => {

        let errorList = [];
        let cleanedData = [];
        console.log(data)

        data.forEach((row, index) => {

            const rowNumber = index + 2; // Excel row (header = 1)

            let hubNameInput = String(getCellValue(row, "HUB") || "").trim();
            let consigneeCityInput = String(getCellValue(row, "CONSIGNEE CITY") || "").trim();
            let stateInput = String(getCellValue(row, "STATE") || "").trim();

            const matchedHub = findByFieldIgnoreCase(hubData, "hub_name", hubNameInput);
            const matchedCity = findByFieldIgnoreCase(consigneeCityData, "city", consigneeCityInput);
            const matchedState = (consigneeCityData || []).find((each) =>
                isSameIgnoreCase(each.city, consigneeCityInput) && isSameIgnoreCase(each.state, stateInput)
            );

            let hubName = matchedHub ? matchedHub.hub_name : hubNameInput.toUpperCase();
            let consigneeCity = matchedCity ? matchedCity.city : consigneeCityInput;
            let state = matchedState ? matchedState.state : stateInput;

            let rowErrors = [];

            // ---- Hub Name ----
            if (!matchedHub) {
                rowErrors.push(`Hub - "${hubNameInput}" does not exist in Hub Master.`);
            }
            if (!matchedCity) {
                rowErrors.push(`Consignee City - "${consigneeCityInput}" does not exist in available cities.`);
            }
            if (!matchedState) {
                rowErrors.push(`State - "${stateInput}" is not mapped to Consignee City - ${consigneeCityInput}`);
            }


            // ---- Collect Errors ----
            if (rowErrors.length > 0) {
                errorList.push({
                    row: rowNumber,
                    errors: rowErrors
                });
            } else {
                cleanedData.push({
                    // loading_port_code: loadingPortCode,
                    // comp_short_code: loadingPortData.find((each) => each.loading_port_code == loadingPortCode),
                    hub_name: hubName,
                    consignee_city: consigneeCity,
                    state: state
                });
            }
        });

        if (cleanedData.length == 0 && errorList.length == 0) {
            errorList.push({
                row: "",
                errors: ["No Data found. All rows are empty"]
            });
        }

        setErrors(errorList);
        setValidData(cleanedData);

        console.log("Valid Data:", cleanedData);
        console.log("Errors:", errorList);
    };




    var errTrueUpload = errors.length > 0 && validData.length > 0
    var disableUpload = validData.length == 0
    const getShortFileName = (name, maxLength = 31) => {
        const lastDot = name.lastIndexOf(".");
        const baseName = name.substring(0, lastDot);
        const extension = name.substring(lastDot);

        if (baseName.length <= maxLength) return name;

        return baseName.substring(0, maxLength) + "..." + extension;
    };
    const closeAlert = () => {
                setShow(false)
            
    }
            const [show, setShow] = useState(false);
            const [basicType, setBasicType] = useState("");
            const [basicTitle, setBasicTitle] = useState("");
    // console.log(formData, stateData)
    const onExportClick = () => {
        if (gridRef.current && gridRef.current.api) {
            // this.gridRef.current.api.exportDataAsCsv();
            gridRef.current.api.exportDataAsExcel({
                fileName: "hub_consignee_city_mapping.xlsx"
            });
        }
    };
    if (pageAccess != 2) {
        allowActionsRef.current = true;
    }
    if (pageAccess == 3) {
        allowDeleteRef.current = true;
    }
    return (
        
        <div className="hub-master-container d-flex flex-column" style={{ height: "100%", overflowY: "auto" }}>
            <SweetAlert
                                        show={show}
                                        type={basicType}
                                        title={basicTitle}
                                        onConfirm={closeAlert}
                                        styles={{zIndex: "10000001"}}
                                    >
                                    </SweetAlert>
            {/* HEADER */}
            <div className="master-header">
                <div className="d-flex align-items-center" style={{ gap: "14px" }}>
                    <div style={{ width: "5px", height: "26px", background: "linear-gradient(180deg,#1976d2,#42a5f5)", borderRadius: "3px", flexShrink: 0 }}></div>
                    <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#1a237e", letterSpacing: "0.2px" }}>Hub Consignee City Mapping</h5>
                </div>

                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    {(selectedRows.length > 0 && allowDeleteRef.current) && <button
                        className="pill-btn pill-btn-danger"
                        onClick={() => { handleDeleteSelected(loadingPortCode) }}
                        disabled={selectedRows.length == 0}
                    >
                        Delete Selected
                    </button>}
                     <button
                        className="pill-btn pill-btn-primary"
                        onClick={() => { onExportClick() }}
                    >
                        Export Data
                    </button>
                    {allowActionsRef.current && <button
                        className="pill-btn pill-btn-success"
                        onClick={() => {
                            setShowSlider(true);
                            var object = loadingPortData.find((each) => each.value == loadingPortCode)
                            setFormData((prevState) => ({
                                ...prevState,
                                loading_port_code: object.value, comp_short_name: object.label
                            }))
                        }}
                    >
                        + Add
                    </button>}
                    {allowActionsRef.current && <button
                        className="pill-btn pill-btn-info"
                        onClick={() => {
                            setErrors([]);
                            setValidData([]);
                            setFileName("");
                            setShowBulkSlider(true);
                        }}
                    >
                        Import Data
                    </button>}
                </div>
            </div>

            {/* GRID */}
            <div className="ag-theme-balham grid-wrapper mt-2" style={{ flex: 1, minHeight: 0 }}>
                <AgGridReact
                    ref={gridRef}
                    modules={modules}
                    rowData={hubMappingData}
                    columnDefs={columnDefs}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        floatingFilter: true
                    }}
                    statusBar={{
                        statusPanels: [
                            { statusPanel: "agSelectedRowCountComponent", align: "left" },
                            { statusPanel: "agFilteredRowCountComponent", align: "center" },
                            { statusPanel: "agTotalRowCountComponent", align: "right" }
                        ]
                    }}
                    sideBar={{
                        toolPanels: [
                            {
                                id: 'columns',
                                labelDefault: 'Columns',
                                iconKey: 'columns',
                                toolPanel: 'agColumnsToolPanel'
                            },
                            {
                                id: 'filters',
                                labelDefault: 'Filters',
                                iconKey: 'filter',
                                toolPanel: 'agFiltersToolPanel'
                            }
                        ],
                        // defaultToolPanel: 'filters'
                    }}
                    animateRows rowSelection={"multiple"}
                    rowHeight={45}
                    headerHeight={45}
                    frameworkComponents={components}
                    onSelectionChanged={() => {
                        if (gridRef && gridRef.current && gridRef.current.api) {
                            const selected = gridRef.current.api.getSelectedRows();
                            setSelectedRows(selected);
                        }
                    }} suppressRowClickSelection={true}
                />
            </div>

            {/* OVERLAY */}
            {showSlider && (
                <div
                    className="slider-overlay"
                    onClick={closeSlider}
                />
            )}
            {showBulkSlider && (
                <div
                    className="slider-overlay"
                    onClick={closeSlider}
                />
            )}

            {/* RIGHT SLIDER */}
            <div className={`hub-slider ${showSlider ? "open" : ""} d-flex flex-column`}>

                <div className="slider-header">
                    {editIndex !== null ? "Edit Hub Consignee City Mapping" : "New Hub Consignee City Mapping"}
                    <span onClick={closeSlider}>✕</span>
                </div>

                <div className="slider-body flex-grow-1 pt-4">

                    <label className="m-0">Loading Plant <span className="text-danger">*</span></label>
                    <Select
                        options={loadingPortData}
                        value={loadingPortData.find((each) => each.value == formData.loading_port_code) || null}
                        onChange={(selected) => {
                            setFormData({ ...formData, loading_port_code: selected.value, comp_short_name: selected.label });
                        }}
                        classNamePrefix="react-select" className="mb-2"
                        isDisabled={true}
                        styles={customStyles}
                    />

                    <label className="m-0">Hub <span className="text-danger">*</span></label>
                    <Select
                        options={hubData}
                        value={hubData.find((each) => each.value == formData.hub_name) || null}
                        onChange={(selected) => {
                            setFormData({ ...formData, hub_name: selected.value });
                        }}
                        classNamePrefix="react-select" className="mb-2"
                        isDisabled={!formData.loading_port_code}
                        styles={customStyles}
                    />

                    <label className="m-0">Consignee City <span className="text-danger">*</span></label>
                    <Select
                        options={uniqueConsigneeCityData}
                        value={uniqueConsigneeCityData.find((each) => each.value == formData.consignee_city) || null}
                        onChange={(selected) => {
                            setFormData({ ...formData, consignee_city: selected.value });
                        }}
                        classNamePrefix="react-select" className="mb-2"
                        isDisabled={!formData.loading_port_code}
                        styles={customStyles}
                    />

                    <label className="m-0">State <span className="text-danger">*</span></label>
                    <Select
                        options={stateData}
                        value={stateData.find((each) => each.value == formData.state) || null}
                        onChange={(selected) => {
                            setFormData({ ...formData, state: selected.value });
                        }}
                        classNamePrefix="react-select" className="mb-2"
                        isDisabled={!formData.loading_port_code || !formData.consignee_city || stateData.length < 2}
                        styles={customStyles}
                    />

                </div>
                <div className="slider-footer">
                    <button
                        className="save-btn" style={{ margin: "0px" }}
                        onClick={handleAdd}
                    >
                        {editIndex !== null ? "Update" : "Save"}
                    </button>
                </div>
            </div>

            <div className={`hub-slider ${showBulkSlider ? "open" : ""} d-flex flex-column`}>

                <div className="slider-header">
                    
                    Import Data
                    <span onClick={closeSlider}>✕</span>
                </div>

                <div className="slider-body flex-grow-1">

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />
                    {/* Upload button */}
                    <div className="d-flex align-items-center gap-3">
                        <button
                            className="btn btn-info d-flex justify-content-center p-3"
                            type="button"
                            onClick={() => fileInputRef.current.click()}
                        >
                            Upload File
                        </button>

                        <a href={require("../../assets/hub_city_consignee_mapping.xlsx")} className="btn btn-secondary d-flex justify-content-center ml-3 p-3 flex-grow-1">
                            Download Sample Template
                        </a>
                    </div>

                    {fileName && (
                        <div style={{ marginTop: "15px", display: "flex", gap: "6px", maxWidth: "410px" }}>
                            <span>Selected File :</span>
                            <span
                                style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    flex: 1,
                                    fontWeight: "bold"
                                }}
                                title={fileName}
                            >
                                {getShortFileName(fileName)}
                            </span>
                        </div>
                    )}


                    {errors.length > 0 && (
                        <>
                            <h6 className="text-danger mt-3">Validation Errors</h6>

                            <div style={{ flexGrow: 1, overflowY: "auto", width: "100%" }}>
                                <table className="table-bordered mb-0 err-table" style={{ width: "100%", positive: "relative" }}>
                                    <thead className="table-light text-center" style={{ positive: "sticky", zIndex: "100", backgroundColor: "#efefef" }}>
                                        <tr>
                                            <th style={{ width: "60px", padding: "9px 15px" }}>Row</th>
                                            <th>Error Description</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {errors.map((err, i) => (
                                            <tr key={i}>
                                                <td className="text-center" style={{ padding: "9px 15px" }}>
                                                    {err.row}
                                                </td>

                                                <td style={{ padding: "9px 15px", wordBreak: "break-word" }}>
                                                    {err.errors.map((e, idx) => (
                                                        <p className="m-0" key={idx}>{e}</p>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                </div>
                <div className="slider-footer">
                    <button
                        className="save-btn" style={{ margin: "0px", width: "auto", padding: "5px 25px", cursor: disableUpload && "not-allowed", opacity: disableUpload && "0.5" }}
                        onClick={handleBulkUpload}
                        disabled={disableUpload}
                    >
                        {errTrueUpload ? "Submit only valid data" : "Submit"}
                    </button>
                </div>

            </div>

            <div className={"dataLoadpageimg " + (loadshow)} style={{zIndex: 100}}>
                <div className="loader-box"><div className="loader-box"><div className="rotate dashed colored"></div></div></div>
            </div>
            <div className={"overlay-part " + (overly)} onClick={onClickHideAll}></div>


        </div>
    );
};

export default HubSourceMapping; 